import { FINANCE_LIMIT_ITEMS, financeUpperRate } from './finance-limits'
import { SPOUSE_AGES } from './spouse-ages'

export const PERSONAL_FIELDS = [
  { key: 'account', automatic: true, label: 'アカウント番号' },
  { key: 'pin', automatic: true, label: 'ピン番号' },
  { key: 'age', automatic: true, label: '年齢', numeric: true, unit: '歳' },
  { key: 'is_married', hidden: true },
  { key: 'spouse_age', automatic: true, label: '配偶者', choices: [
    { text: 'いない', value: 'none' },
    ...SPOUSE_AGES.map(age => ({ text: String(age), value: String(age) }))
  ] },
  { key: 'child1_age', automatic: true, label: '子ども1の年齢', numeric: true, unit: '歳' },
  { key: 'child2_age', automatic: true, label: '子ども2の年齢', numeric: true, unit: '歳' },
  { key: 'gross_yearly_income', label: '年間総収入', numeric: true, yen: true },
  { key: 'monthly_tax', label: '月額の税金', numeric: true, yen: true },
  { key: 'monthly_annuity_insurance', label: '月額の年金保険料', numeric: true, yen: true },
  { key: 'monthly_social_insurance', label: '月額の健康・社会保険料', numeric: true, yen: true },
  { key: 'nmi', label: 'NMI（月間純所得・自動計算）', automatic: true, numeric: true, yen: true },
  { key: 'lucky_card', label: 'ラッキーカード', automatic: true, numeric: true, yen: true, signed: true }
]

export function personalSheetSpouseValue(sheet) {
  return sheet.fields.is_married === 'いない' ? 'none' : (sheet.fields.spouse_age || '')
}

export function createPersonalSheet() {
  return {
    totals_confirmed: false,
    fields: Object.fromEntries(PERSONAL_FIELDS.map(({ key }) => [key, ''])),
    amounts: Object.fromEntries(FINANCE_LIMIT_ITEMS.map(({ key }) => [key, '']))
  }
}

// Keep blank distinct from an explicitly entered zero. Accept pasted full-width digits.
export function parseSheetAmount(value, signed = false) {
  if (value === null || value === undefined) return null
  const text = String(value).normalize('NFKC').replace(/,/g, '').trim()
  if (!(signed ? /^-?\d+$/ : /^\d+$/).test(text)) return null
  const amount = Number(text)
  return Number.isSafeInteger(amount) && Math.abs(amount) <= 999999999 ? amount : null
}

// Format only valid money values; preserve blanks and mistakes as learning notes.
export function formatSheetMoney(value, signed = false) {
  const amount = parseSheetAmount(value, signed)
  return amount === null ? (value == null ? '' : String(value)) : amount.toLocaleString('ja-JP')
}

export function personalSheetIdentity(loginResponse = {}) {
  const persona = loginResponse.persona || {}
  const text = value => value == null ? '' : String(value)
  const married = [0, 1].includes(Number(persona.is_married)) && text(persona.is_married).trim() !== ''
  const child = key => Object.keys(persona).length === 0 ? '' : text(persona[key]).trim() === '' ? 'いない' : text(persona[key])
  return {
    account: text(persona.account), pin: text(persona.pin), age: text(persona.age),
    is_married: married ? (Number(persona.is_married) === 0 ? 'いない' : 'いる') : '',
    spouse_age: married && Number(persona.is_married) === 1 ? text((loginResponse.queryStringParameters || {}).spouse_age) : '',
    child1_age: child('child1_age'), child2_age: child('child2_age')
  }
}

export function personalSheetAutoValues(sheet, loginResponse = {}) {
  const fields = { ...sheet.fields, ...personalSheetIdentity(loginResponse) }
  const inputs = ['gross_yearly_income', 'monthly_tax', 'monthly_annuity_insurance', 'monthly_social_insurance'].map(key => parseSheetAmount(fields[key]))
  let nmi = ''
  let nmiError = ''
  if (inputs.every(value => value !== null)) {
    const net = inputs[0] / 12 - inputs[1] - inputs[2] - inputs[3]
    if (net < 0) nmiError = '控除額が月収を超えています。入力を確認してください。'
    else nmi = String(Math.round(net))
  }
  const identifier = value => {
    const text = String(value == null ? '' : value).normalize('NFKC').trim()
    return /^\d+$/.test(text) ? text.replace(/^0+(?=\d)/, '') : ''
  }
  const persona = loginResponse.persona || {}
  const account = identifier(fields.account)
  const pin = identifier(fields.pin)
  const matches = !!account && !!pin && account === identifier(persona.account) && pin === identifier(persona.pin)
  const lucky = matches ? parseSheetAmount(persona.lucky_card, true) : null
  const luckyHint = matches
    ? (lucky === null ? 'ラッキーカードの金額を取得できませんでした。' : 'ログイン中の情報から自動入力しています。')
    : (account && pin ? 'ログイン中のアカウント・ピン番号と一致しません。' : 'アカウント番号とピン番号が一致すると自動入力します。')
  return { nmi, nmiError, lucky: lucky === null ? '' : String(lucky), luckyHint }
}

// Derive on every read so restored sessions and Complete cannot retain stale auto values.
export function resolvedPersonalSheet(state) {
  const sheet = state.personal_sheet || createPersonalSheet()
  const auto = personalSheetAutoValues(sheet, state.login_response)
  return { ...sheet, fields: { ...sheet.fields, ...personalSheetIdentity(state.login_response), nmi: auto.nmi, lucky_card: auto.lucky } }
}

function rate(value, fallback) {
  if (value === null || value === undefined || value === '') return fallback
  const number = Number(value)
  return Number.isFinite(number) && number >= 0 ? number : fallback
}

export function personalSheetRows(sheet, loginResponse = {}, target = 'ja') {
  const nmi = parseSheetAmount(sheet.fields.nmi)
  return FINANCE_LIMIT_ITEMS.map(item => {
    const limits = loginResponse[`limits_${item.key}`] || {}
    const lowerRate = rate(limits.lower_limit, item.lower)
    const upperRate = item.key === 'saving' ? null : financeUpperRate(item, target, rate(limits.upper_limit, item.upperRaw))
    return {
      ...item, lowerRate, upperRate,
      // Match percentage checks for integer-yen inputs, including unusual handwritten NMI values.
      lowerAmount: nmi === null ? null : Math.ceil(nmi * lowerRate / 100),
      upperAmount: nmi === null || upperRate === null ? null : Math.floor(nmi * upperRate / 100),
      amount: parseSheetAmount(sheet.amounts[item.key])
    }
  })
}

export function personalSheetTotals(sheet) {
  const amounts = FINANCE_LIMIT_ITEMS.map(({ key }) => parseSheetAmount(sheet.amounts[key]))
  const entered = amounts.filter(value => value !== null)
  const total = entered.reduce((sum, value) => sum + value, 0)
  const nmi = parseSheetAmount(sheet.fields.nmi)
  const luckyInput = sheet.fields.lucky_card
  const lucky = luckyInput == null || String(luckyInput).trim() === '' ? 0 : parseSheetAmount(luckyInput, true)
  return { total, entered: entered.length, balance: nmi === null || lucky === null ? null : nmi + lucky - total }
}

// Calculation checks completeness only; selected amounts are checked at submission.
export function personalSheetCalculationErrors(sheet) {
  const errors = { fields: {}, amounts: {} }
  for (const key of ['gross_yearly_income', 'monthly_tax', 'monthly_annuity_insurance', 'monthly_social_insurance', 'nmi']) {
    if (parseSheetAmount(sheet.fields[key]) === null) errors.fields[key] = '計算に必要な金額を正しく入力してください。'
  }
  for (const { key } of FINANCE_LIMIT_ITEMS) {
    if (parseSheetAmount(sheet.amounts[key]) === null) errors.amounts[key] = '全費目を入力してください。支出がない場合は0円です。'
  }
  return errors
}

export function canDisplayPersonalSheetTotals(sheet) {
  const errors = personalSheetCalculationErrors(sheet)
  return sheet.totals_confirmed === true && Object.keys(errors.fields).length === 0 && Object.keys(errors.amounts).length === 0
}

export function personalSheetSubmission(state) {
  if (state.target !== 'ja') return {}
  const sheet = resolvedPersonalSheet(state)
  // Raw input strings preserve blank/zero and learning mistakes. Avoid JSON null/boolean
  // values because the existing submit Lambda parses its request with Python eval.
  return { personal_sheet: {
    version: 1,
    fields: Object.fromEntries(PERSONAL_FIELDS.map(({ key }) => [key, String(sheet.fields[key] == null ? '' : sheet.fields[key])])),
    amounts: Object.fromEntries(FINANCE_LIMIT_ITEMS.map(({ key }) => [key, String(sheet.amounts[key] == null ? '' : sheet.amounts[key])]))
  } }
}
